<template>
  <div v-if="inputDataIsReady" class="d-flex flex-column">
    <!-- SUMMARY (START) -->
    <GmbDisplayHeading isSecondary text="Résumé de l'étude">
      <template>
        <FdText colorTheme3 fa>
          <i class="fa-solid fa-chart-pie"/>
        </FdText>
      </template>

    </GmbDisplayHeading>

    <div v-if="userCanAccessPdfExporter" class="row no-gutters">
      <div class="col-9 pr-3">
        <b-button
            class="d-flex p-1 justify-content-center align-items-center text-size-reset rounded-1"
            style="float: right"
            variant="primary"
            @click="exportToReportingSection()"
        >
          <FdText fa><i class="fas fa-download"/></FdText>
          <FdText class="mx-2" tag="span">Ajouter au PDF</FdText>
        </b-button>
      </div>
    </div>

    <div class="d-flex mt-3"/>

    <div class="row no-gutters">
      <div class="col-9 pr-3">
        <div class="d-flex mx-0 container-xl px-0 flex-column">
          <div class="row no-gutters">
            <div class="col-7 pr-3">
              <GmbSection :displayLevel="1" title="Population">
                <template #picto>
                  <FdText colorTheme4 fa>
                    <i class="fas fa-people-group"/>
                  </FdText>
                </template>

                <GmbRow
                >
                  <div class="row m-n1 py-1 align-items-center no-gutters">
                    <div class="col-auto p-1">
                      <FdText class="gmb-column-label" tag="label"
                      >Nombre de sites sélectionnés
                      </FdText
                      >
                    </div>
                    <div class="col-auto p-1">
                      <GmbDisplayCell :value="organizationCount"/>
                    </div>
                  </div>

                  <div class="row m-n1 py-1 align-items-center no-gutters">
                    <div class="col-auto p-1">
                      <FdText class="gmb-column-label" tag="label"
                      >Effectif connu suivant filtres sélectionnés
                      </FdText
                      >
                    </div>
                    <div class="col-auto p-1">
                      <GmbDisplayCell
                          :value="formatThousandsSeparator(individuals.length)"
                      />
                    </div>
                  </div>

                  <div class="row m-n1 py-1 align-items-center no-gutters">
                    <div class="col-auto p-1">
                      <FdText class="gmb-column-label" tag="label"
                      >Dont effectif avec transport type renseigné
                      </FdText
                      >
                    </div>
                    <div class="col-auto p-1">
                      <GmbDisplayCell
                          :value="
                          formatThousandsSeparator(
                            countOfIndividualsHavingTransportType
                          )
                        "
                      />
                    </div>
                  </div
                  >
                </GmbRow>
              </GmbSection>
            </div>

            <div class="col-5 pl-3" :style="{ minHeight: '256px', maxHeight: '356px' }">
              <GmbSection
                  :displayLevel="1"
                  lockHeader
                  title="Liste des sites sélectionnés"
              >
                <template #picto>
                  <FdText colorTheme4 fa>
                    <i class="fas fa-building"/>
                  </FdText>
                </template>

                <template #infoSticker>
                  <GmbSticker :text="organizations.length"/>
                </template>

                <div
                    v-for="(organization, idx) in organizations"
                    :class="[
                    'row no-gutters',
                    { 'mt-2 border-top pt-2': idx != 0 },
                  ]"
                    :key="idx"
                >
                  <div class="col">
                    <GmbRow>
                      <FdText tag="span">{{
                          `${idx + 1} - ${organization.label}`
                        }}
                      </FdText>
                      <FdText v-if="organization.selectedAddressIndex != undefined" tag="span">{{
                          organization.addressList[
                              organization.selectedAddressIndex
                              ].address +
                          "\n" +
                          organization.addressList[
                              organization.selectedAddressIndex
                              ].postcode +
                          " " +
                          organization.addressList[
                              organization.selectedAddressIndex
                              ].city
                        }}
                      </FdText>
                    </GmbRow>
                  </div>
                </div>
              </GmbSection>
            </div>
          </div>
        </div>
      </div>

      <div class="col-3 border-left pl-3">
        <!-- References -->
      </div>
    </div>
    <!-- SUMMARY (END) -->

    <div class="d-flex mt-3 border-top border-light pt-3"/>

    <!-- CALCULATION METHOD (START) -->
    <GmbDisplayHeading isSecondary text="Méthode de calcul">
      <template>
        <FdText colorTheme3 fa>
          <i class="fa-solid fa-calculator"/>
        </FdText>
      </template>
    </GmbDisplayHeading>

    <div class="d-flex mt-3"/>

    <div class="row no-gutters" :style="{ minHeight: '256px' }">
      <div class="d-flex col-9 pr-3 flex-column">
        <div class="row flex-grow-1 h-0 no-gutters">
          <div class="d-flex mx-0 container-xl px-0 flex-column">
            <GmbRow>
              <div class="row m-n1 py-1 align-items-center no-gutters">
                <div class="col-auto p-1">
                  <FdText class="gmb-column-label" tag="label"
                  >Facteur d'extrapolation
                  </FdText
                  >
                </div>
                <div class="col-auto p-1">
                  <GmbDisplayCell
                      :value="toFixed(theoreticalWorkforceExtrapolationFactor)"
                  />
                </div>
              </div>

              <div class="row m-n1 py-1 align-items-center no-gutters">
                <div class="col-auto p-1">
                  <FdText class="gmb-column-label" tag="label"
                  >Utiliser les modes de transport connus pour les
                    calculs
                  </FdText
                  >
                </div>

                <div class="col-auto p-1">
                  <b-form-checkbox
                      v-model="useKnownTransportTypesForCalculations"
                      name="check-button"
                      switch
                  />
                </div>
              </div>

              <div class="row m-n1 py-1 align-items-center no-gutters">
                <div class="col p-1">
                  <FdText tag="span"
                  >* Si non coché, la voiture est prise comme base de calcul
                    pour les distances et les temps
                  </FdText
                  >
                </div>
              </div>

              <div class="row m-n1 py-1 align-items-center no-gutters">
                <div class="col-auto p-1">
                  <FdText class="gmb-column-label" tag="label"
                  >Nombre moyen de jours travaillés par an
                  </FdText
                  >
                </div>

                <div class="col-auto p-1">
                  <b-form-input
                      v-bind="positiveFloatInputParams"
                      :value="workingDaysPerYear"
                      @update="
                      (value) =>
                        (workingDaysPerYear = preventNegativeValue(value))
                    "
                  />
                </div>
              </div>

              <div class="row m-n1 py-1 align-items-center no-gutters">
                <div class="col-auto p-1">
                  <FdText class="gmb-column-label" tag="label"
                  >Utiliser les données télétravail connues pour les calculs
                  </FdText
                  >
                </div>

                <div class="col-auto p-1">
                  <b-form-checkbox
                      v-model="useKnownTeleworkHalfDaysForCalculations"
                      name="check-button"
                      switch
                  />
                </div>
              </div>

              <div class="row m-n1 py-1 align-items-center no-gutters">
                <div class="col-auto p-1">
                  <FdText class="gmb-column-label" tag="label"
                  >Journées entièrement télétravaillées par semaine
                  </FdText
                  >
                </div>

                <div class="col-auto p-1">
                  <b-form-input
                      type="range" min="0" max="5"
                      :value="teleworkFullDaysPerWeek"
                      :disabled="useKnownTeleworkHalfDaysForCalculations"
                      @update="
                      (value) =>
                        (teleworkFullDaysPerWeek = value)
                    "
                  />
                  <span>{{ !useKnownTeleworkHalfDaysForCalculations ? teleworkFullDaysPerWeek : "&nbsp;" }}</span>
                </div>
              </div>

            </GmbRow>
          </div>
        </div>
      </div>

      <div class="col-3 border-left pl-3">
        <div class="d-flex mx-0 container-xl px-0 flex-column">
          <GmbRow>
            <div class="row m-n1 py-1 no-gutters">
              <div class="col-auto p-1">
                <GmbDisplayCell
                    pictofAPictoCssClasses="fas fa-earth"
                    title="Temps de travail moyen"
                    value="220"
                    unity="jours par an"
                />
              </div>
            </div>
          </GmbRow>
        </div>
      </div>
    </div>
    <!-- CALCULATION METHOD (END) -->

    <div class="d-flex mt-3 border-top border-light pt-3"/>

    <!-- MODAL SHARE (START) -->
    <GmbDisplayHeading isSecondary text="Répartition modale">
      <template>
        <FdText colorTheme3 fa>
          <i class="fas fa-car"/>
        </FdText>
      </template>
    </GmbDisplayHeading>

    <div class="d-flex mt-3"/>

    <div class="row no-gutters">
      <div class="col-9 pr-3">
        <div class="d-flex mx-0 container-xl px-0 flex-column">
          <div class="row no-gutters">
            <div class="col-7 pr-3">
              <GmbRow>
                <div class="row mt-3 no-gutters">
                  <div class="col">
                    <FdScroll x>
                      <GmbStatisticsHorizontalBarChart
                          ref="modalShareChart"
                          :chartData="modalShareChartData"
                          :options="horizontalBarChartOptions"
                          :width="500"
                      />
                    </FdScroll>
                  </div>
                </div>
              </GmbRow>
            </div>

            <div class="col-5 border-left pl-3">
              <GmbRow>
                <!-- Custom Modal Share values -->
                <div class="row m-n1 py-1 align-items-center no-gutters">
                  <div class="col-auto p-1">
                    <FdText class="gmb-column-label" tag="label"
                    >Répartition modale personnalisée
                    </FdText
                    >
                  </div>

                  <div class="col-auto p-1">
                    <b-form-checkbox
                        v-model="useCustomModalShareValues"
                        :disabled="useKnownTransportTypesForCalculations"
                        name="check-button"
                        switch
                        @change="onUseCustomModalShareValuesChange"
                    />
                  </div>
                </div>

                <div v-if="useKnownTransportTypesForCalculations" class="row mt-1 no-gutters">
                  <div class="col-12">
                    <div class="d-flex mx-0 container-xl px-0 flex-column">
                      <GmbNotificationMessage
                          message='Il n&#39;est pas possible de spécifier une répartition modale personnalisée si l&#39;option "Utiliser les modes de transport connus pour les calculs" est cochée.'
                          noLength
                          type="info"
                      />
                    </div>
                  </div>
                  <div class="col-3 border-left pl-3"></div>
                </div>

                <!-- Custom Modal Share fields -->
                <div
                    v-if="useCustomModalShareValues"
                    class="row mt-1 no-gutters"
                >
                  <div class="col-auto">
                    <div
                        v-for="(transportType, idx) in consideredTransportTypes"
                        class="row m-n1 py-1 align-items-center no-gutters"
                        :key="idx"
                    >
                      <div class="col-5 p-1 justify-content-center">
                        <GmbDisplayCell
                            :pictofAPictoCssClasses="
                            transportTypeIcons[transportType]
                          "
                            noBackground
                            :title="transportTypeLabels[transportType]"
                        />
                      </div>

                      <div class="col-3 p-1 justify-content-center">
                        <b-form-input
                            v-bind="positiveFloatInputParams"
                            :value="
                            userSubmittedModalSharePercentages[transportType]
                          "
                            @update="
                            (value) =>
                              (userSubmittedModalSharePercentages[transportType] =
                                preventNegativeValue(value))
                          "
                        />
                      </div>

                      <div class="col-4 p-1 justify-content-center">
                        <GmbNotificationMessage
                            :isPlaceholder="
                            isValidPositiveNumber(
                              userSubmittedModalSharePercentages[transportType]
                            )
                          "
                            message="Valeur incorrecte"
                        />
                      </div>
                    </div>

                    <div class="row mt-1 no-gutters">
                      <div class="col">
                        <b-button
                            class="d-flex p-1 justify-content-center align-items-center text-size-reset rounded-1"
                            variant="primary"
                            @click="resetUserSubmittedModalSharePercentages"
                        >
                          <FdText class="mx-2" tag="span">Réinitialiser</FdText>
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>
              </GmbRow>
            </div>
          </div>
        </div>
      </div>

      <div class="col-3 border-left pl-3">
        <div class="d-flex mx-0 container-xl px-0 flex-column">
          <GmbRow>
            <div class="row m-n1 py-1 no-gutters">
              <div class="col-auto p-1">
                <GmbDisplayCell
                    href="https://www.ademe.fr"
                    title="Agence de la Transition Écologique (ADEME)"
                    subTitle="www.ademe.fr"
                />
              </div>

              <div class="col-auto p-1">
                <GmbDisplayCell
                    href="https://monimpacttransport.fr/"
                    :title="'Découvrir l\'impact de vos déplacements sur le climat\nMon impact transport - ADEME'"
                    subTitle="monimpacttransport.fr"
                />
              </div>
            </div>
          </GmbRow>
        </div>
      </div>
    </div>
    <!-- MODAL SHARE (END) -->

    <div class="d-flex mt-3 border-top border-light pt-3"/>

    <!-- IMPACT (START) -->
    <GmbDisplayHeading isSecondary text="Impacts des trajets domicile-travail">
      <template>
        <FdText colorTheme3 fa>
          <i class="fa-solid fa-burst"/>
        </FdText>
      </template>
    </GmbDisplayHeading>

    <div class="d-flex mt-3"/>

    <div v-if="!useKnownTransportTypesForCalculations" class="d-flex mt-3"/>

    <GmbSection :displayLevel="1" title="Distances parcourues">
      <template #picto>
        <FdText colorTheme4 fa>
          <i class="fas fa-ruler"/>
        </FdText>
      </template>

      <div v-if="!useKnownTransportTypesForCalculations" class="row no-gutters">
        <div class="col-9 pr-3">
          <div class="d-flex mx-0 container-xl px-0 flex-column">
            <GmbRow>
              <GmbNotificationMessage
                  message='L&#39;option "Utiliser les modes de transport connus pour les calculs" étant décochée, on considère que tous les individus prennent leur VOITURE, pour ces calculs de distance et de temps (donc aucun impact de la "répartition modale" ci-dessus).'
                  noLength
                  type="info"
              />
            </GmbRow>
          </div>
        </div>
        <div class="col-3 border-left pl-3"></div>
      </div>

      <div class="row no-gutters">
        <div class="col-9 pr-3">
          <div class="d-flex mx-0 container-xl px-0 flex-column">
            <div class="row no-gutters">
              <div class="col-7 pr-3">
                <GmbSection title="Point de vue : Site">
                  <template #picto>
                    <FdText fa>
                      <i class="fas fa-building"/>
                    </FdText>
                  </template>

                  <GmbRow>
                    <div class="row m-n1 py-1 no-gutters">
                      <div class="col-auto p-1">
                        <GmbDisplayCell
                            state="neutral"
                            title="Distance totale parcourue"
                            unity="km / jour"
                            :value="
                            roundForDisplay(
                              globalNavigationStats.globalDistanceKmPerDay
                            )
                          "
                        />
                      </div>
                      <div class="col-auto p-1">
                        <GmbDisplayCell
                            state="neutral"
                            title="Distance totale parcourue"
                            unity="km / an"
                            :value="
                            roundForDisplay(
                              globalNavigationStats.globalDistanceKmPerYear
                            )
                          "
                        />
                      </div>
                      <div class="col-auto p-1">
                        <GmbDisplayCell
                            pictofAPictoCssClasses="fas fa-earth"
                            state="neutral"
                            title="Équivalent à"
                            unity="tour(s) du monde / an"
                            :value="
                            toFixed(
                              globalNavigationStats.globalWorldToursPerYear
                            )
                          "
                        />
                      </div>
                    </div>
                  </GmbRow>
                  <GmbRow v-if="useKnownTransportTypesForCalculations">
                    <div class="row mt-3 no-gutters">
                      <div class="col">
                        <FdScroll x>
                          <GmbStatisticsHorizontalBarChart
                              ref="yearlyDistancePerTransportTypeChart"
                              :chartData="yearlyDistancePerTransportTypeChartData"
                              :options="horizontalBarChartOptions"
                              :width="500"
                          />
                        </FdScroll>
                      </div>
                    </div>
                  </GmbRow>
                </GmbSection>
              </div>

              <div class="col-5 pl-3">
                <GmbSection title="Point de vue : Individu (hors télétravail)">
                  <template #picto>
                    <FdText fa>
                      <i class="fas fa-people-group"/>
                    </FdText>
                  </template>

                  <!-- <GmbSection title="Pour l'effectif global"> -->
                  <GmbRow>
                    <div class="row m-n1 py-1 no-gutters">
                      <div class="col-6 p-1">
                        <GmbDisplayCell
                            state="neutral"
                            title="Distance moyenne des trajets domicile-travail"
                            unity="km"
                            :value="
                            roundForDisplay(
                              globalNavigationStats.averageDistanceKmPerTrip
                            )
                          "
                        />
                      </div>
                      <div class="col-6 p-1">
                        <GmbDisplayCell
                            state="neutral"
                            title="Distance médiane des trajets domicile-travail"
                            unity="km"
                            :value="roundForDisplay(allTripDistancesMedian)"
                        />
                      </div>
                    </div>

                    <div class="row m-n1 py-1 no-gutters" v-if="!useKnownTransportTypesForCalculations">
                      <div class="col-6 p-1">
                        <GmbDisplayCell
                            state="neutral"
                            title="Temps de trajet moyen domicile-travail"
                            unity="mn"
                            :value="
                            roundForDisplay(
                              globalNavigationStats.averageDurationMnPerTrip
                            )
                          "
                        />
                      </div>
                    </div>
                  </GmbRow>
                  <!-- </GmbSection> -->

                  <div class="d-flex mt-3 border-top pt-3" v-if="useKnownTransportTypesForCalculations"/>

                  <!-- <GmbSection title="Pour chaque individu"> -->
                  <GmbRow v-if="useKnownTransportTypesForCalculations">
                    <div class="row no-gutters">
                      <div class="col-auto">
                        <!-- Table header -->
                        <div class="row m-n1 py-1 align-items-end no-gutters">
                          <div
                              class="d-flex col-4 p-1 flex-column justify-content-center"
                          />

                          <div
                              class="d-flex col-4 p-1 flex-column justify-content-center"
                          >
                            <GmbDisplayCell
                                noBackground
                                title="Distance trajets domicile-travail A/R"
                            />
                          </div>

                          <div
                              class="d-flex col-4 p-1 flex-column justify-content-center"
                          >
                            <GmbDisplayCell
                                noBackground
                                title="Temps trajets domicile-travail A/R"
                            />
                          </div>
                        </div>

                        <!-- Table body -->
                        <div
                            v-for="(
                            transportType, idx
                          ) in consideredTransportTypes"
                            class="row m-n1 py-1 align-items-center no-gutters"
                            :key="idx"
                        >
                          <template>
                            <div class="col-4 p-1 justify-content-center">
                              <GmbDisplayCell
                                  noBackground
                                  :pictofAPictoCssClasses="
                                  transportTypeIcons[transportType]
                                "
                                  :title="transportTypeLabels[transportType]"
                              />
                            </div>

                            <div class="col-4 p-1 justify-content-center">
                              <GmbDisplayCell
                                  :value="
                                  roundForDisplay(
                                    calculusConstants.trips_per_day *
                                      extrapolatedPerTransportTypeStats[
                                        transportType
                                      ].averageDistanceKmPerTrip
                                  )
                                "
                                  unity="km / jour"
                              />
                            </div>

                            <div class="col-4 p-1 justify-content-center">
                              <GmbDisplayCell
                                  :value="
                                  roundForDisplay(
                                    calculusConstants.trips_per_day *
                                      extrapolatedPerTransportTypeStats[
                                        transportType
                                      ].averageDurationMnPerTrip
                                  )
                                "
                                  unity="mn / jour"
                              />
                            </div>
                          </template>
                        </div>
                      </div>
                    </div>
                  </GmbRow>
                  <!-- </GmbSection> -->
                </GmbSection>
              </div>
            </div>
          </div>
        </div>

        <div class="col-3 border-left pl-3">
          <div class="d-flex mx-0 container-xl px-0 flex-column">
            <GmbRow>
              <div class="row m-n1 py-1 no-gutters">
                <div class="col-auto p-1">
                  <!-- Header DisplayCell -->
                </div>
              </div>
            </GmbRow>
          </div>
        </div>
      </div>
    </GmbSection>

    <div class="d-flex mt-3 border-top pt-3"/>

    <GmbSection :displayLevel="1" title="Impacts environnementaux">
      <template #picto>
        <FdText colorTheme4 fa>
          <i class="fas fa-tree"/>
        </FdText>
      </template>

      <div v-if="!useKnownTransportTypesForCalculations" class="row no-gutters">
        <div class="col-9 pr-3">
          <div class="d-flex mx-0 container-xl px-0 flex-column">
            <GmbRow>
              <GmbNotificationMessage
                  message='L&#39;option "Utiliser les modes de transport connus pour les calculs" étant décochée, on ne considère que la part des individus prenant leur VOITURE (en accord avec la répartition modale ci-dessus) pour ces impacts environnementaux.'
                  noLength
                  type="info"
              />
            </GmbRow>
          </div>
        </div>
        <div class="col-3 border-left pl-3"></div>
      </div>

      <div class="row no-gutters">
        <div class="col-9 pr-3">
          <div class="d-flex mx-0 container-xl px-0 flex-column">
            <div class="row no-gutters">
              <div class="col-7 pr-3">
                <GmbSection title="Point de vue : Site">
                  <template #picto>
                    <FdText fa>
                      <i class="fas fa-building"/>
                    </FdText>
                  </template>

                  <GmbRow>
                    <div class="row m-n1 py-1 no-gutters">
                      <div class="col-auto p-1">
                        <GmbDisplayCell
                            state="neutral"
                            title="Emissions totales CO2"
                            unity="tonnes par an"
                            :value="
                            toFixed(
                              globalNavigationStats.globalCo2EmissionKgPerYear /
                                1000
                            )
                          "
                        />
                      </div>
                    </div>
                    <div class="row m-n1 py-1 no-gutters">
                      <div class="col-auto p-1">
                        <GmbDisplayCell
                            state="neutral"
                            title="Consommation totale énergétique des automobilistes"
                            unity="kWh / an"
                            :value="
                            roundForDisplay(
                              globalNavigationStats.globalCarEnergyConsumptionKwhPerYear
                            )
                          "
                        />
                      </div>

                      <div class="col-auto p-1">
                        <GmbDisplayCell
                            state="neutral"
                            title="Équivalent à la consommation énergétique annuelle de"
                            unity="m² de bureaux"
                            :value="
                            roundForDisplay(
                              globalNavigationStats.globalCarEnergyConsumptionDesktopM2Equivalent
                            )
                          "
                        />
                      </div>
                    </div>

                    <div class="row mt-1 no-gutters">
                      <div class="col-auto">
                        <FdScroll x>
                          <GmbStatisticsPolarAreaChart
                              ref="yearlyCo2EmissionPerTransportTypeChart"
                              v-if="yearlyCo2EmissionPerTransportTypeChartData"
                              :chartData="
                              yearlyCo2EmissionPerTransportTypeChartData
                            "
                              :options="doughnutChartOptions"
                              :height="500"
                          />
                        </FdScroll>
                      </div>
                    </div>
                  </GmbRow>
                </GmbSection>
              </div>

              <div class="col-5 pl-3">
                <GmbSection title="Point de vue : Individu">
                  <template #picto>
                    <FdText fa>
                      <i class="fas fa-people-group"/>
                    </FdText>
                  </template>

                  <!-- <GmbSection title="Pour l'effectif global"> -->
                  <GmbRow>
                    <div class="row m-n1 py-1 no-gutters">
                      <div class="col-auto p-1">
                        <GmbDisplayCell
                            state="neutral"
                            title="Emissions moyennes CO2"
                            unity="kg / an"
                            :value="
                            toFixed(
                              globalNavigationStats.averageCo2EmissionKgPerYear
                            )
                          "
                        />
                      </div>
                    </div>
                  </GmbRow>
                  <!-- </GmbSection> -->

                  <div class="d-flex mt-3 border-top pt-3" v-if="useKnownTransportTypesForCalculations"/>

                  <!-- <GmbSection title="Pour chaque individu"> -->
                  <GmbRow v-if="useKnownTransportTypesForCalculations">
                    <div class="row no-gutters">
                      <div class="col-auto">
                        <!-- Table header -->
                        <div class="row m-n1 py-1 align-items-end no-gutters">
                          <div
                              class="d-flex col-6 p-1 flex-column justify-content-center"
                          />

                          <div
                              class="d-flex col-6 p-1 flex-column justify-content-center"
                          >
                            <GmbDisplayCell
                                noBackground
                                title="Emissions moyennes CO2"
                                unity="kg / an"
                            />
                          </div>
                        </div>

                        <!-- Table body -->
                        <div
                            v-for="(
                            transportType, idx
                          ) in consideredTransportTypes"
                            class="row m-n1 py-1 align-items-center no-gutters"
                            :key="idx"
                        >
                          <template>
                            <div class="col-6 p-1 justify-content-center">
                              <GmbDisplayCell
                                  noBackground
                                  :pictofAPictoCssClasses="
                                  transportTypeIcons[transportType]
                                "
                                  :title="transportTypeLabels[transportType]"
                              />
                            </div>

                            <div class="col-6 p-1 justify-content-center">
                              <GmbDisplayCell
                                  :value="
                                  toFixed(
                                    fromDayToYear(
                                      extrapolatedPerTransportTypeStats[
                                        transportType
                                      ].averageCo2EmissionKgPerDay
                                    )
                                  )
                                "
                                  unity="kg / an"
                              />
                            </div>
                          </template>
                        </div>
                      </div>
                    </div>
                  </GmbRow>
                </GmbSection>
              </div>
            </div>
          </div>
        </div>

        <div class="col-3 border-left pl-3">
          <div class="d-flex mx-0 container-xl px-0 flex-column">
            <GmbRow>
              <div class="row m-n1 py-1 no-gutters">
                <div class="col-auto p-1">
                  <GmbDisplayCell
                      title="Base CO2 voiture"
                      unity="kg / km"
                      value="0,193"
                  />
                </div>
                <div class="col-auto p-1">
                  <GmbDisplayCell
                      title="Base CO2 VAE"
                      unity="kg / km"
                      value="0,002"
                  />
                </div>
                <div class="col-auto p-1">
                  <GmbDisplayCell
                      title="Base CO2 EPDM"
                      unity="kg / km"
                      value="0,002"
                  />
                </div>
                <div class="col-auto p-1">
                  <GmbDisplayCell
                      title="Base Eco Énergie"
                      unity="kW / km"
                      value="0,617"
                  />
                </div>
              </div>
            </GmbRow>
          </div>
        </div>
      </div>
    </GmbSection>

    <div class="d-flex mt-3 border-top pt-3"/>

    <GmbSection :displayLevel="1" title="Budget et coûts">
      <template #picto>
        <FdText colorTheme4 fa>
          <i class="fas fa-coins"/>
        </FdText>
      </template>

      <div class="row no-gutters">
        <div class="col-9 pr-3">
          <div class="d-flex mx-0 container-xl px-0 flex-column">
            <div class="row no-gutters">
              <div class="col">
                <GmbRow>
                  <!-- Custom Modal Share values -->
                  <div class="row m-n1 py-1 align-items-center no-gutters">
                    <div class="col-auto p-1">
                      <FdText class="gmb-column-label" tag="label"
                      >Passer en mode expert
                      </FdText
                      >
                    </div>

                    <div class="col-auto p-1">
                      <b-form-checkbox
                          v-model="isExpertMode"
                          name="check-button"
                          switch
                      />
                    </div>
                  </div>

                  <!-- Custom Modal Share fields -->
                  <div v-if="isExpertMode" class="row mt-1 no-gutters">
                    <div class="col-auto">
                      <div class="row no-gutters">
                        <div
                            v-for="([label, fieldName], idx) in expertModeFields"
                            class="col-4"
                            :key="idx"
                        >
                          <div
                              class="row m-n1 py-1 align-items-center no-gutters"
                          >
                            <div class="col-4 p-1 justify-content-center">
                              <GmbDisplayCell noBackground :title="label"/>
                            </div>

                            <div class="col-4 p-1 justify-content-center">
                              <b-form-input
                                  v-bind="positiveFloatInputParams"
                                  :value="calculusConstants[fieldName]"
                                  @update="
                                  (value) =>
                                    (calculusConstants[fieldName] =
                                      preventNegativeValue(value))
                                "
                              />
                            </div>

                            <div class="col-4 p-1 justify-content-center">
                              <GmbNotificationMessage
                                  :isPlaceholder="
                                  isValidPositiveNumber(
                                    calculusConstants[fieldName]
                                  )
                                "
                                  message="Valeur incorrecte"
                                  type="error"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="row mt-1 no-gutters">
                        <div class="col">
                          <b-button
                              class="d-flex p-1 justify-content-center align-items-center text-size-reset rounded-1"
                              variant="primary"
                              @click="resetUserSubmittedCalculusConstants"
                          >
                            <FdText class="mx-2" tag="span"
                            >Réinitialiser
                            </FdText
                            >
                          </b-button>
                        </div>
                      </div>
                    </div>
                  </div>
                </GmbRow>
              </div>
            </div>

            <div class="d-flex mt-3 border-top pt-3"/>

            <div class="row no-gutters">
              <div class="col-7 pr-3">
                <GmbSection title="Point de vue : Site">
                  <template #picto>
                    <FdText fa>
                      <i class="fas fa-building"/>
                    </FdText>
                  </template>

                  <GmbRow>
                    <div class="row m-n1 py-1 no-gutters">
                      <div class="col-auto p-1">
                        <GmbDisplayCell
                            state="neutral"
                            title="Budget mobilités alternatives"
                            unity="€ / an"
                            :value="
                            roundForDisplay(
                              costStatsForEmployerAndIndividual.totalYearlyCostEuroForEmployerForAlternativeTransportTypes
                            )
                          "
                        />
                      </div>

                      <div class="col-auto p-1">
                        <GmbDisplayCell
                            state="neutral"
                            title="Budget lié à l'usage de la voiture"
                            unity="€ / an"
                            :value="
                            roundForDisplay(
                              costStatsForEmployerAndIndividual
                                .perTransportTypeYearlyCostsEuroForEmployer.CAR
                            )
                          "
                        />
                      </div>

                      <div class="col-auto p-1">
                        <GmbDisplayCell
                            state="neutral"
                            title="Impact foncier du stationnement voiture"
                            unity="m²"
                            :value="
                            toFixed(
                              globalNavigationStats.globalCarParkingSpaceImpactM2

                            )
                          "
                        />
                      </div>
                    </div>

                    <div class="row mt-1 no-gutters">
                      <div class="col-auto">
                        <FdScroll x>
                          <GmbStatisticsPolarAreaChart
                              ref="yearlyEmployerCostPerTransportType"
                              :chartData="
                              yearlyEmployerCostPerTransportTypeChartData
                            "
                              :height="500"
                              :options="doughnutChartOptions"
                          />
                        </FdScroll>
                      </div>
                    </div>
                  </GmbRow>
                </GmbSection>
              </div>

              <div class="col-5 pl-3">
                <GmbSection title="Point de vue : Individu">
                  <template #picto>
                    <FdText fa>
                      <i class="fas fa-people-group"/>
                    </FdText>
                  </template>

                  <GmbRow>
                    <div class="row m-n1 py-1 no-gutters">
                      <div class="col-auto p-1">
                        <GmbDisplayCell
                            state="neutral"
                            title="Budget transport moyen"
                            unity="€ / an"
                            :value="
                            roundForDisplay(
                              costStatsForEmployerAndIndividual.averageYearlyCostForIndividual
                            )
                          "
                        />
                      </div>
                    </div>
                  </GmbRow>

                  <div class="d-flex mt-3 border-top pt-3"/>

                  <GmbRow>
                    <div class="row no-gutters">
                      <div class="col-auto">
                        <!-- Table header -->
                        <div class="row m-n1 py-1 align-items-end no-gutters">
                          <div
                              class="d-flex col-6 p-1 flex-column justify-content-center"
                          />

                          <div
                              class="d-flex col-6 p-1 flex-column justify-content-center"
                          >
                            <GmbDisplayCell
                                noBackground
                                title="Budget"
                                unity="€ / an"
                            />
                          </div>
                        </div>

                        <!-- Table body -->
                        <div
                            v-for="(
                            transportType, idx
                          ) in consideredTransportTypes"
                            class="row m-n1 py-1 align-items-center no-gutters"
                            :key="idx"
                        >
                          <div class="col-6 p-1 justify-content-center">
                            <GmbDisplayCell
                                noBackground
                                :pictofAPictoCssClasses="
                                transportTypeIcons[transportType]
                              "
                                :title="transportTypeLabels[transportType]"
                            />
                          </div>

                          <div class="col-6 p-1 justify-content-center">
                            <GmbDisplayCell
                                :value="
                                roundForDisplay(
                                  costStatsForEmployerAndIndividual
                                    .perTransportTypeYearlyCostsEuroForIndividual[
                                    transportType
                                  ]
                                )
                              "
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </GmbRow>
                </GmbSection>
              </div>
            </div>
          </div>
        </div>

        <div class="col-3 border-left pl-3">
          <div class="d-flex mx-0 container-xl px-0 flex-column">
            <GmbRow>
              <div class="row m-n1 py-1 no-gutters">
                <div class="col-auto p-1">
                  <GmbDisplayCell
                      title="Coût moyen du stationnement"
                      unity="€ / place de parking"
                      value="1 250"
                  />
                </div>
                <div class="col-auto p-1">
                  <GmbDisplayCell
                      title="Coût moyen voiture"
                      unity="€ / km"
                      value="0,33"
                  />
                </div>
              </div>
            </GmbRow>
          </div>
        </div>
      </div>
    </GmbSection>
    <!-- IMPACT (END) -->

    <div class="d-flex mt-3 border-top border-light pt-3"/>

    <!-- CAR TRAVEL DISTANCES (START) -->
    <GmbDisplayHeading text="Distances Voiture">
      <template>
        <FdText colorTheme3 fa>
          <i class="fas fa-ruler"/>
        </FdText>
      </template>
    </GmbDisplayHeading>

    <div class="d-flex mt-3"/>

    <div class="row no-gutters" :style="{ minHeight: '400px' }">
      <div class="d-flex col-9 pr-3 flex-column">
        <div class="row flex-grow-1 h-0 no-gutters">
          <div class="d-flex mx-0 container-xl px-0 flex-column">
            <FdScroll x>
              <GmbRow>
                <GmbStatisticsHorizontalBarChart
                    ref="carDistanceCategoryChart"
                    v-if="carDistanceCategoryChartData"
                    :chartData="carDistanceCategoryChartData"
                    :height="400"
                    :options="horizontalBarChartOptions"
                    :width="550"
                />
                <!--doughnut-chart :width="600" :chartData="modalShareChartData" :options="doughnutChartOptions"></doughnut-chart-->
              </GmbRow>
            </FdScroll>
          </div>
        </div>
      </div>
      <div class="col-3 border-left pl-3">
        <!-- References -->
      </div>
    </div>
    <!-- CAR TRAVEL DISTANCES (END) -->

    <div class="d-flex mt-3 border-top border-light pt-3"/>

    <!-- TRAVEL DURATIONS (START) -->
    <GmbDisplayHeading
        isSecondary
        text="Temps de trajet théoriques (pourcentage)"
    >
      <template>
        <FdText colorTheme3 fa>
          <i class="fas fa-clock"/>
        </FdText>
      </template>
    </GmbDisplayHeading>

    <div class="d-flex mt-3"/>

    <div class="row no-gutters" :style="{ minHeight: '320px' }">
      <div class="d-flex col-9 pr-3 flex-column">
        <div class="row flex-grow-1 h-0 no-gutters">
          <div class="d-flex mx-0 container-xl px-0 flex-column">
            <FdScroll x>
              <GmbRow>
                <GmbStatisticsHorizontalBarChart
                    ref="travelDurationCategoryChart"
                    v-if="travelDurationCategoryChartData"
                    :chartData="travelDurationCategoryChartData"
                    :height="300"
                    :options="horizontalBarChartOptions"
                    :width="550"
                />
                <!--doughnut-chart :width="600" :chartData="modalShareChartData" :options="doughnutChartOptions"></doughnut-chart-->
              </GmbRow>
            </FdScroll>
          </div>
        </div>
      </div>
      <div class="col-3 border-left pl-3">
        <!-- References -->
      </div>
    </div>
    <!-- TRAVEL DURATIONS (END) -->

    <div class="d-flex mt-3 border-top border-light pt-3"/>

    <!-- TRANSPORT COMPARISONS (START) -->
    <GmbDisplayHeading
        isSecondary
        text="Comparaison des temps de parcours (pourcentage)"
    >
      <template>
        <FdText colorTheme3 fa>
          <i class="fas fa-stopwatch"/>
        </FdText>
      </template>
    </GmbDisplayHeading>

    <div class="d-flex mt-3"/>

    <div class="row no-gutters" :style="{ minHeight: '320px' }">
      <div class="d-flex col-9 pr-3 flex-column">
        <div class="row flex-grow-1 h-0 no-gutters">
          <div class="d-flex mx-0 container-xl px-0 flex-column">
            <FdScroll x>
              <GmbRow>
                <GmbStatisticsHorizontalBarChart
                    ref="transportComparisonCategoryChart"
                    v-if="transportComparisonCategoryChartData"
                    :chartData="transportComparisonCategoryChartData"
                    :height="300"
                    :options="horizontalBarChartOptions"
                    :width="550"
                />
                <!--doughnut-chart :width="600" :chartData="modalShareChartData" :options="doughnutChartOptions"></doughnut-chart-->
              </GmbRow>
            </FdScroll>
          </div>
        </div>
      </div>
      <div class="col-3 border-left pl-3">
        <!-- References -->
      </div>
    </div>
    <!-- TRANSPORT COMPARISONS (END) -->
  </div>
</template>

<script>
import Chart from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

import FdScroll from "@/flexdesign/Scroll";
import FdText from "@/flexdesign/Text";

import GmbDisplayCell from "@/widgets/DisplayCell";
import GmbDisplayHeading from "@/widgets/DisplayHeading";
import GmbNotificationMessage from "@/widgets/NotificationMessage";
import GmbRow from "@/widgets/Row";
import GmbSection from "@/widgets/Section";
import GmbSticker from "@/widgets/Sticker";

// import GmbStatisticsDoughnutChart from "@/views/Observatory/Statistics/StatisticsDoughnutChart";
import GmbStatisticsHorizontalBarChart from "@/views/Observatory/Statistics/StatisticsHorizontalBarChart";
import GmbStatisticsPolarAreaChart from "@/views/Observatory/Statistics/StatisticsPolarAreaChart";

import {openToast, getCanvasContentAsPngBase64} from "@/helpers.js";

Chart.plugins.register(ChartDataLabels);

const CAR_DISTANCE_ATTRIBUTE_SLUG =
    "route_assessment-car_travel_distance_category";

export default {
  name: "GmbStatistics",

  components: {
    FdScroll,
    FdText,

    GmbDisplayCell,
    GmbDisplayHeading,
    GmbNotificationMessage,
    GmbRow,
    GmbSection,
    GmbSticker,

    // GmbStatisticsDoughnutChart,
    GmbStatisticsHorizontalBarChart,
    GmbStatisticsPolarAreaChart,
  },

  props: {
    mapColorizerCategories: Array,
    importedIndividualWorkforce: Number,
    individuals: Array,
    miscEnumerations: Object,
    organizations: Array,
    theoreticalIndividualWorkforce: Number,
    userCanAccessPdfExporter: Boolean,
  },

  data() {
    let average_individuals_by_car_sharing = 2; // ARBITRARY divisor
    let car_co2_kg_per_km = 0.193; // FIXME LATER get this value from webservice->miscEnumerations
    let car_sharing_co2_kg_per_km = car_co2_kg_per_km / average_individuals_by_car_sharing;

    return {
      expertModeFields: [
        [
          "Coût annuel moyen abonnement TC \n(€ / an)",
          "public_transport_yearly_subscription_price_euros",
        ],
        [
          "Taux prise en charge abonnement TC\n(%)",
          "public_transport_subscription_coverage_rate_percent",
        ],
        [
          "Plafond annuel FMD ou IKV Vélo / VAE\n(€ / an)",
          "bike_and_electric_bike_yearly_fmd_limit_euros",
        ],
        [
          "Plafond annuel FMD ou IKC covoiturage\n(€ / an)",
          "car_sharing_yearly_fmd_limit_euros",
        ],
        [
          "Plafond annuel FMD ou IK EPDM\n(€ / an)",
          "plev_yearly_fmd_limit_euros",
        ],
        ["Indemnité marche\n(€ / an)", "walk_yearly_allowance_euros"],
        ["Nombre place(s) de stationnement", "car_parking_space_count"],
        [
          "Valeur locative pour une place de parking\n(€ / an)",
          "car_parking_space_price_euros",
        ],
        [
          "Indemnité km voiture\n(€ / km)",
          "car_mileage_allowance_euros_per_km",
        ],
        [
          "Forfait indemnité voiture\n(€ / an)",
          "car_yearly_compensation_package_euros",
        ],
      ],

      initialCalculusConstants: {
        trips_per_day: 2, // Aller-retour
        world_circumference_km: 40000,
        average_individuals_by_car_sharing: average_individuals_by_car_sharing,

        // NO VALUE HERE for public_transport_co2_emission_kg !
        car_co2_kg_per_km: car_co2_kg_per_km,
        car_sharing_co2_kg_per_km: car_sharing_co2_kg_per_km,
        bike_co2_kg_per_km: 0,
        electric_bike_co2_kg_per_km: 0.002,
        plev_co2_kg_per_km: 0.002,
        walk_co2_kg_per_km: 0,

        car_energy_kwh_per_km: 0.617, // WHAT IS THAT ??? for CAR only ????
        car_cost_euros_per_km: 0.33,
        car_parking_area_m2: 12.5,

        desktop_energy_consumption_yearly_kwh_per_m2: 124,

        // EXPERT MODE VARIABLES BELOW //

        car_mileage_allowance_euros_per_km: 0, // Used instead of car_yearly_compensation_package_euros if set!
        car_yearly_compensation_package_euros: 0,

        car_parking_space_count: 0,
        car_parking_space_price_euros: 996,

        public_transport_yearly_subscription_price_euros: 600,
        public_transport_subscription_coverage_rate_percent: 50,

        bike_and_electric_bike_yearly_fmd_limit_euros: 0, // Plafond FMD (ou IKV) Vélo / VAE annuel
        car_sharing_yearly_fmd_limit_euros: 0, // Plafond FMD (ou IKC) Covoiturage annuel
        plev_yearly_fmd_limit_euros: 0, // Plafond FMD (ou IK) EPDM annuel
        walk_yearly_allowance_euros: 0, // Indemnité marche annuel
      },

      defaultUserSubmittedModalSharePercentages: {
        CAR: 71,
        PUBLIC_TRANSPORT: 16,
        CAR_SHARING: 3,
        BIKE: 2,
        ELECTRIC_BIKE: 1,
        PLEV: 1,
        WALK: 6,
      },

      isExpertMode: false,

      positiveFloatInputParams: {
        type: "number",
        number: true,
        step: "any",
        debounce: "500",
        //min: "0", - doesn't seem to work
        //max: "100",
      },

      useCustomModalShareValues: false,
      useCustomModalShareValuesBackup: undefined,

      userSubmittedCalculusConstants: {}, // To be overridden by initialCalculusConstants

      useKnownTransportTypesForCalculations: false, // Most projets don't have real TransportType values in DB
      useKnownTeleworkHalfDaysForCalculations: false,
      userSubmittedModalSharePercentages: {}, // To be initialized with defaultUserSubmittedModalSharePercentages

      // Calculus variables directly edited by global form controls
      workingDaysPerYear: 220,
      teleworkFullDaysPerWeek: 0,

      transportTypeIcons: {
        // FIXME LATER DEDUPLICATE FROM API?
        CAR: "fas fa-car",
        PUBLIC_TRANSPORT: "fas fa-bus",
        CAR_SHARING: "fas fa-users",
        BIKE: "fas fa-bicycle",
        ELECTRIC_BIKE: "fas fa-motorcycle",
        PLEV: "fas fa-person-skating",
        WALK: "fas fa-person-walking",
      },

      doughnutChartOptions: {
        // ALSO USED FOR POLAR AREA CHARTS
        // BEWARE, The doughnut, pie, and polar area charts override the legend defaults.
        //  Their options are defined in Chart.overrides[type].plugins.legend.
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          labels: {
            fontColor: "white",
            fontSize: 14,
          },
          position: "right",
        },
        scale: {
          display: false,
        },

        plugins: {
          // Change options for ALL labels of THIS CHART
          datalabels: {
            color: "black",
            formatter: function (value, context) {
              return context.chart.data.datalabels[context.dataIndex];
            },
          },
        },
      },

      horizontalBarChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false,
          /*labels: {
                fontColor: "rose",
                fontSize: 15
            },
            position: "right",*/
        },
        scales: {
          xAxes: [
            {
              stacked: true,
              display: false,
              ticks: {
                fontColor: "white",
              },
            },
          ],
          yAxes: [
            {
              barThickness: 35,
              stacked: true,
              gridLines: {
                zeroLineWidth: 0,
                color: "rgba(0, 0, 0, 0)",
              },
              ticks: {
                fontColor: "white",
              },
            },
          ],
        },
        layout: {
          padding: 30, // So that datalabels don't go out the Canvas
        },
        plugins: {
          /*legend: {
                  position: 'right',
          },*/
          // Change options for ALL labels of THIS CHART
          datalabels: {
            anchor: "end",
            align: "right",
            color: "white",
            formatter: function (value, context) {
              if (!context.chart.data.datalabels) return "";
              return context.chart.data.datalabels[context.dataIndex];
            },
          },
        },
      },
    };
  },

  computed: {
    inputDataIsReady() {
      return this.individuals && this.organizations;
    },

    consideredTransportTypes() {
      return this.miscEnumerations.TransportType.map((obj) => obj.value);
    },

    calculusConstants() {
      if (this.isExpertMode) return this.userSubmittedCalculusConstants;
      return this.initialCalculusConstants;
    },

    countOfIndividualsHavingTransportType() {
      let individualsHavingTransportType = this.individuals.filter(
          (individual) => individual.transport_type != ""
      );
      return individualsHavingTransportType.length;
    },

    currentlyActiveModalShareStats() {
      let usedModalShareStatsSource; // Beware, different formats must be supported by this!

      if (this.useKnownTransportTypesForCalculations) {
        usedModalShareStatsSource = Object.fromEntries(
            Object.entries(this.extrapolatedPerTransportTypeStats).map(entry => [
              entry[0], entry[1].extrapolatedTotalConcernedIndividualsCount
            ])
        );
        //console.log(">>>usedModalShareStatsSource", usedModalShareStatsSource);
      } else if (this.useCustomModalShareValues) {
        usedModalShareStatsSource = this.userSubmittedModalSharePercentages;
      } else {
        usedModalShareStatsSource = this.defaultUserSubmittedModalSharePercentages;
      }

      return this._getNormalizedModalShareStats(usedModalShareStatsSource);
    },

    organizationCount() {
      return this.organizations.length;
    },

    theoreticalWorkforceExtrapolationFactor() {
      /* MAIN factor to go from IMPORTED count to THEORETICAL WORKFORCE count */
      if (!this.importedIndividualWorkforce) return 1;
      return (
          this.theoreticalIndividualWorkforce / this.importedIndividualWorkforce
      );
    },

    // BELOW IS THE PIPELINE TO GENERATE STATS //

    rawMedianAndPerTransportTypeStats() {
      return this._getRawMedianAndPerTransportTypeStats(
          this.individuals
      );
    },

    rawPerTransportTypeStats() {
      return this.rawMedianAndPerTransportTypeStats
          .perTransportTypeStats;
    },

    allTripDistancesMedian() {
      return this.rawMedianAndPerTransportTypeStats
          .allTripDistancesMedian;
    },

    extrapolatedPerTransportTypeStats() {
      return this._getExtrapolatedPerTransportTypeStats(
          this.rawPerTransportTypeStats,
          this.theoreticalWorkforceExtrapolationFactor
      );
    },

    globalNavigationStats() {
      return this._getGlobalNavigationStats(  // FIXME make it private like all "computed backends"
          this.extrapolatedPerTransportTypeStats
      );
    },

    costStatsForEmployerAndIndividual() {
      let selectedForCostsextrapolatedPerTransportTypeStats = this.extrapolatedPerTransportTypeStats;

      let costStatsForEmployerAndIndividual =
          this._getCostStatsForEmployerAndIndividual(
              this.extrapolatedPerTransportTypeStats,
              this.globalNavigationStats.globalConcernedIndividualCount,
          );

      return costStatsForEmployerAndIndividual;
    },

    // BELOW IS DATA FOR CHARTS //

    transportTypeLabels() {
      return Object.fromEntries(
          this.miscEnumerations.TransportType.map((obj) => [obj.value, obj.text])
      );
    },

    modalShareChartData() {
      let stats = this.currentlyActiveModalShareStats;

      let backgroundColor = [];
      let labels = [];
      let data = [];
      let datalabels = [];

      for (let transportType of this.consideredTransportTypes) {
        backgroundColor.push(
            this.miscEnumerations.TrafficTypeColors[transportType]
        );
        labels.push(this.transportTypeLabels[transportType]);
        data.push(stats[transportType].modalShareAbsolute);
        datalabels.push(`${stats[transportType].modalSharePercentage}%`);
      }

      return {
        labels: labels,
        datalabels: datalabels, //["Green 3%", "Red 4%", "Blue 5%"],
        datasets: [
          {
            /* When use submits its own modal share value, they can exceed 100%, it's OK, but we need a name for them */
            label: this.useKnownTransportTypesForCalculations
                ? "Individus"
                : "Part modale",
            //label: labels,
            //fontColor: 'white',
            backgroundColor: backgroundColor, //["#41B883", "#E46651", "#00D8FF"],
            data: data, //[1, 10, 5]
          },
        ],
      };
    },

    _carDistanceColorizerOrNull() {
      let mapColorizerCategoryForTransports = this.mapColorizerCategories[3];
      let distanceMapColorizers = mapColorizerCategoryForTransports.mapColorizers[0];
      if (
          !distanceMapColorizers ||
          distanceMapColorizers.identifier != CAR_DISTANCE_ATTRIBUTE_SLUG
      )
        return null; // No Car Distance attribute for this project!
      return distanceMapColorizers;
    },

    carDistanceCategoryOrNullForDataExport() {
      let distanceMapColorizers = this._carDistanceColorizerOrNull;
      if (!distanceMapColorizers)
        return null;

      let chartItems = [];

      for (let criterion of distanceMapColorizers.criteria) {
        chartItems.push([criterion.label, criterion.individualCount]);
      }

      return {
        chart_label: "Distance voiture",
        chart_items: chartItems
      }
    },

    carDistanceCategoryChartData() {

      let distanceMapColorizers = this._carDistanceColorizerOrNull;
      if (!distanceMapColorizers)
        return null;

      let backgroundColor = [];
      let labels = [];
      let data = [];

      for (let criterion of distanceMapColorizers.criteria) {
        backgroundColor.push(distanceMapColorizers.colorMapper[criterion.slug]);
        labels.push(criterion.label);
        data.push(criterion.individualCount);
      }

      let percentageArray = this.getPercentageArray(data);
      let datalabels = percentageArray.map((val) => `${val}%`);

      return {
        labels: labels,
        datalabels: datalabels,
        datasets: [
          {
            label: "Individus",
            backgroundColor: backgroundColor,
            data: data,
          },
        ],
      };
    },

    transportComparisonCategoryChartData() {
      let mapColorizerForComparisons = this.mapColorizerCategories[4];
      return this._buildStackedChartDataFromMapColorizers(
          mapColorizerForComparisons.mapColorizers,
          this.individuals.length
      );
    },

    _allDurationColorizers() {
      let durationMapColorizers;
      let mapColorizerCategoryForTransports = this.mapColorizerCategories[3];

      let distanceMapColorizers = mapColorizerCategoryForTransports.mapColorizers[0];
      if (
          distanceMapColorizers &&
          distanceMapColorizers.identifier == CAR_DISTANCE_ATTRIBUTE_SLUG
      ) {
        // Existing Distance attribute for this project!
        durationMapColorizers =
            mapColorizerCategoryForTransports.mapColorizers.slice(1);
      } else {
        durationMapColorizers = mapColorizerCategoryForTransports.mapColorizers;
      }
      return durationMapColorizers
    },

    carDurationCategoriesForDataExport() {
      let durationMapColorizers = this._allDurationColorizers;

      let durationCategories = [];

      for (let durationMapColorizer of durationMapColorizers) {

        let chartItems = [];
        for (let criterion of durationMapColorizer.criteria) {
          chartItems.push([criterion.label, criterion.individualCount]);
        }

        durationCategories.push({
          chart_label: durationMapColorizer.label,
          chart_items: chartItems
        });
      }

      return durationCategories;
    },

    travelDurationCategoryChartData() {

      let durationMapColorizers = this._allDurationColorizers; // Never null

      return this._buildStackedChartDataFromMapColorizers(
          durationMapColorizers,
          this.individuals.length
      );
    },

    yearlyCo2EmissionPerTransportTypeChartData() {
      if (!this.useKnownTransportTypesForCalculations) return null; // Our numbers are wrong then

      let stats = this.extrapolatedPerTransportTypeStats;

      let backgroundColor = [];
      let labels = [];
      let dataAbsolute = [];

      for (let transportType of this.consideredTransportTypes) {
        backgroundColor.push(this.miscEnumerations.TrafficTypeColors[transportType]);
        labels.push(this.transportTypeLabels[transportType]);

        let Co2EmissionKgPerDay = stats[transportType].extrapolatedTotalCo2EmissionKgPerDay;
        let Co2EmissionKgPerYear = this.fromDayToYear(Co2EmissionKgPerDay);

        dataAbsolute.push(this.round(Co2EmissionKgPerYear));
      }

      let percentageArray = this.getPercentageArray(dataAbsolute);
      let datalabels = percentageArray.map((val) => `${val}%`);

      return {
        labels: labels,
        datalabels: datalabels,
        datasets: [
          {
            label: "CO2 (kg)",
            backgroundColor: backgroundColor,
            data: dataAbsolute,
          },
        ],
      };
    },

    yearlyDistancePerTransportTypeChartData() {
      let stats = this.extrapolatedPerTransportTypeStats;

      let backgroundColor = [];
      let labels = [];
      let dataAbsolute = [];

      for (let transportType of this.consideredTransportTypes) {
        backgroundColor.push(this.miscEnumerations.TrafficTypeColors[transportType]);
        labels.push(this.transportTypeLabels[transportType]);
        dataAbsolute.push(
            this.round(
                this.fromDayToYear(
                    stats[transportType].extrapolatedTotalDistanceKmPerDay
                )
            )
        );
      }

      let percentageArray = this.getPercentageArray(dataAbsolute);
      let datalabels = percentageArray.map((val) => `${val}%`);

      return {
        labels: labels,
        datalabels: datalabels,
        datasets: [
          {
            label: "Distance (km)",
            backgroundColor: backgroundColor,
            data: dataAbsolute,
          },
        ],
      };
    },

    yearlyEmployerCostPerTransportTypeChartData() {
      let stats = this.costStatsForEmployerAndIndividual;

      let backgroundColor = [];
      let labels = [];
      let dataAbsolute = [];

      for (let transportType of this.consideredTransportTypes) {
        backgroundColor.push(this.miscEnumerations.TrafficTypeColors[transportType]);
        labels.push(this.transportTypeLabels[transportType]);
        dataAbsolute.push(
            this.round(stats.perTransportTypeYearlyCostsEuroForEmployer[transportType])
        );
      }

      let percentageArray = this.getPercentageArray(dataAbsolute);
      let datalabels = percentageArray.map((val) => `${val}%`);

      return {
        labels: labels,
        datalabels: datalabels,
        datasets: [
          {
            label: "Prix (€)",
            backgroundColor: backgroundColor,
            data: dataAbsolute,
          },
        ],
      };
    },
  },

  watch: {
    useKnownTransportTypesForCalculations: function (value) {
      if (value) {
        this.useCustomModalShareValuesBackup = this.useCustomModalShareValues;
        this.useCustomModalShareValues = false;
      } else
        this.useCustomModalShareValues = this.useCustomModalShareValuesBackup;
    },
  },

  beforeCreate() {
  },

  created() {
    this.resetUserSubmittedCalculusConstants();
    this.resetUserSubmittedModalSharePercentages();
  },

  beforeMount() {
  },

  mounted() {
  },

  beforeUpdate() {
  },

  updated() {
  },

  beforeUnmount() {
  },

  unmounted() {
  },

  methods: {

    _getNormalizedModalShareStats(usedModalShareStats) {

      let allConcernedIndividualsCount = 0;
      let normalizedModalShareStats = {};

      for (let [_, transportTypeStat] of Object.entries(usedModalShareStats)) {
        allConcernedIndividualsCount += transportTypeStat;
      }

      for (let [transportType, transportTypeStat] of Object.entries(usedModalShareStats)) {

        let modalShareRatio = this.safeDivision(transportTypeStat, allConcernedIndividualsCount);

        normalizedModalShareStats[transportType] = {
          modalShareAbsolute: this.round(transportTypeStat),
          modalSharePercentage: this.round(modalShareRatio * 100),
          modalShareRatio: modalShareRatio,
        };
      }
      // console.log(" >>> extrapolatedModalShareStats", normalizedModalShareStats);
      return normalizedModalShareStats;
    },

    _getRawMedianAndPerTransportTypeStats(individuals) {
      // BEWARE THESE ARE DISTANCES/DURATIONS PER TRIP!!

      let useKnownTransportTypesForCalculations = this.useKnownTransportTypesForCalculations;

      let perTransportTypeStats = {}; // Maps transport-type to Object of aggregated data
      let transportType;

      let allTripDistancesArray = [];

      let tripsPerDay = this.calculusConstants.trips_per_day;

      for (transportType of this.consideredTransportTypes) {
        perTransportTypeStats[transportType] = {
          totalDistanceKmPerTrip: 0,
          totalDistanceKmPerDay: 0,  // Takes telework into account
          individualCountForDistance: 0,
          totalDurationMnPerTrip: 0,
          totalDurationMnPerDay: 0,  // Takes telework into account
          individualCountForDuration: 0,
          totalCo2EmissionKgPerTrip: 0,
          totalCo2EmissionKgPerDay: 0,  // Takes telework into account
          individualCountForCo2Emission: 0,
          totalConcernedIndividualCount: 0, // All filtered individuals that ARE SUPPOSED TO USE that transport type
        };
      }

      for (let individual of individuals) {

        let teleworkFullDaysPerWeek;
        if (this.useKnownTeleworkHalfDaysForCalculations) {
          teleworkFullDaysPerWeek = parseInt(individual.full_telework_days)
          if (isNaN(teleworkFullDaysPerWeek) || teleworkFullDaysPerWeek < 0 || teleworkFullDaysPerWeek > 5)
            teleworkFullDaysPerWeek = 0;
        } else {
          teleworkFullDaysPerWeek = parseInt(this.teleworkFullDaysPerWeek);  // Global value
        }

        if (useKnownTransportTypesForCalculations)
          transportType = individual.transport_type || "";
        else
          transportType = "CAR"; // FORCED FALLBACK

        let raw_direction_values =
            individual.cachedCurrentRouteAssessement.raw_direction_values;

        if (!raw_direction_values) {
          continue;  // cachedCurrentRouteAssessement is {} if no route was found
        }

        if (!transportType) {
          continue;
        }

        let tripToDayRatio = tripsPerDay * ((5 - teleworkFullDaysPerWeek) / 5)

        let currentTransportTypeStats = perTransportTypeStats[transportType];

        currentTransportTypeStats["totalConcernedIndividualCount"] += 1; // Always

        // DISTANCE CALCULATIONS

        let distanceFieldName = transportType.toLowerCase() + "_distance_km";
        let individualDistanceValue = raw_direction_values[distanceFieldName];
        if (individualDistanceValue !== null) {
          currentTransportTypeStats["totalDistanceKmPerTrip"] += individualDistanceValue;
          currentTransportTypeStats["totalDistanceKmPerDay"] += individualDistanceValue * tripToDayRatio;
          currentTransportTypeStats["individualCountForDistance"] += 1;
          allTripDistancesArray.push(individualDistanceValue); // For median calculation
        }

        // DURATION CALCULATIONS

        let durationFieldName = transportType.toLowerCase() + "_duration_mn";
        let individualDurationValue = raw_direction_values[durationFieldName];
        if (individualDurationValue !== null) {
          currentTransportTypeStats["totalDurationMnPerTrip"] += individualDurationValue;
          currentTransportTypeStats["totalDurationMnPerDay"] += individualDurationValue * tripToDayRatio;
          currentTransportTypeStats["individualCountForDuration"] += 1;
        }

        // CO2 EMISSION CALCULATIONS

        let individualCo2EmissionValue;

        if (transportType == "PUBLIC_TRANSPORT") {
          // SPECIAL CO2 CASE
          individualCo2EmissionValue = raw_direction_values.public_transport_co2_emission_kg;
        } else {
          let co2_kg_per_km = this.calculusConstants[transportType.toLowerCase() + "_co2_kg_per_km"];
          individualCo2EmissionValue =
              individualDistanceValue !== null ? individualDistanceValue * co2_kg_per_km : null;
        }

        if (individualCo2EmissionValue !== null) {
          currentTransportTypeStats["totalCo2EmissionKgPerTrip"] += individualCo2EmissionValue;
          currentTransportTypeStats["totalCo2EmissionKgPerDay"] += individualCo2EmissionValue * tripToDayRatio;
          currentTransportTypeStats["individualCountForCo2Emission"] += 1;
        }
      }

      if (!useKnownTransportTypesForCalculations) {
        // All the data was pushed to CAR only, time to respread it between transport types
        // according to arbitrarily defined modal shares (default or user-tweaked ones)

        let carValues = structuredClone(perTransportTypeStats.CAR);
        let currentlyActiveModalShareStats = this.currentlyActiveModalShareStats;  // Should NOT loop

        for (transportType of this.consideredTransportTypes) {
          let consideredTransportType = perTransportTypeStats[transportType];
          let transportTypeRatio = currentlyActiveModalShareStats[transportType].modalShareRatio;

          for (const [key, value] of Object.entries(carValues)) {
            if (transportType != "CAR" && key.includes("Co2"))
              consideredTransportType[key] = null;  // Drop broken CO2 calculus for non-CAR transports, in this case
            else
              consideredTransportType[key] = value * transportTypeRatio;
          }
        }
      }

      let allTripDistancesMedian = this.getMedian(allTripDistancesArray);

      return {
        allTripDistancesMedian: allTripDistancesMedian,
        perTransportTypeStats: perTransportTypeStats,
      };
    },

    _getExtrapolatedPerTransportTypeStats(
        perTransportTypeStats,
        mainExtrapolationFactor
    ) {
      // BEWARE THESE ARE DISTANCES/DURATIONS PER TRIP!!

      let extrapolatedPerTransportTypeStats = {}; // Maps transport-type to Object of aggregated data

      for (let [transportType, transportTypeStats] of Object.entries(
          perTransportTypeStats
      )) {
        let averageDistanceKmPerTrip = 0;
        let averageDistanceKmPerDay = 0;
        let extrapolatedTotalDistanceKmPerTrip = 0;
        let extrapolatedTotalDistanceKmPerDay = 0

        let averageDurationMnPerTrip = 0;
        let extrapolatedTotalDurationMnPerTrip = 0;
        let extrapolatedTotalDurationMnPerDay = 0;

        let averageCo2EmissionKgPerTrip = 0;
        let averageCo2EmissionKgPerDay = 0;
        let extrapolatedTotalCo2EmissionKgPerTrip = 0;
        let extrapolatedTotalCo2EmissionKgPerDay = 0;

        let extrapolatedTotalConcernedIndividualsCount =
            transportTypeStats.totalConcernedIndividualCount *
            mainExtrapolationFactor;

        // We extrapolate twice, first to compensate individuals with a known transport-type but no direction data for it,
        // and then to estimate the rest of workforce (individuals not imported, or having no transport-type set)

        if (transportTypeStats.individualCountForDistance) {
          averageDistanceKmPerTrip = transportTypeStats.totalDistanceKmPerTrip / transportTypeStats.individualCountForDistance;
          averageDistanceKmPerDay = transportTypeStats.totalDistanceKmPerDay / transportTypeStats.individualCountForDistance;

          let specificExtrapolationFactor = extrapolatedTotalConcernedIndividualsCount / transportTypeStats.individualCountForDistance;
          extrapolatedTotalDistanceKmPerTrip = transportTypeStats.totalDistanceKmPerTrip * specificExtrapolationFactor;
          extrapolatedTotalDistanceKmPerDay = transportTypeStats.totalDistanceKmPerDay * specificExtrapolationFactor;
        }

        if (transportTypeStats.individualCountForDuration) {
          averageDurationMnPerTrip = transportTypeStats.totalDurationMnPerTrip / transportTypeStats.individualCountForDuration;

          let specificExtrapolationFactor = extrapolatedTotalConcernedIndividualsCount / transportTypeStats.individualCountForDuration;
          extrapolatedTotalDurationMnPerTrip = transportTypeStats.totalDurationMnPerTrip * specificExtrapolationFactor;
          extrapolatedTotalDurationMnPerDay = transportTypeStats.totalDurationMnPerDay * specificExtrapolationFactor;
        }

        if (transportTypeStats.individualCountForCo2Emission) {
          averageCo2EmissionKgPerTrip = transportTypeStats.totalCo2EmissionKgPerTrip / transportTypeStats.individualCountForCo2Emission;
          averageCo2EmissionKgPerDay = transportTypeStats.totalCo2EmissionKgPerDay / transportTypeStats.individualCountForCo2Emission;

          let specificExtrapolationFactor = extrapolatedTotalConcernedIndividualsCount / transportTypeStats.individualCountForCo2Emission;
          extrapolatedTotalCo2EmissionKgPerTrip = transportTypeStats.totalCo2EmissionKgPerTrip * specificExtrapolationFactor;
          extrapolatedTotalCo2EmissionKgPerDay = transportTypeStats.totalCo2EmissionKgPerDay * specificExtrapolationFactor;
        }

        extrapolatedPerTransportTypeStats[transportType] = {
          averageDistanceKmPerTrip: averageDistanceKmPerTrip,
          averageDistanceKmPerDay: averageDistanceKmPerDay,
          extrapolatedTotalDistanceKmPerTrip: extrapolatedTotalDistanceKmPerTrip,
          extrapolatedTotalDistanceKmPerDay: extrapolatedTotalDistanceKmPerDay,

          averageDurationMnPerTrip: averageDurationMnPerTrip,
          extrapolatedTotalDurationMnPerTrip: extrapolatedTotalDurationMnPerTrip,
          extrapolatedTotalDurationMnPerDay: extrapolatedTotalDurationMnPerDay,

          averageCo2EmissionKgPerTrip: averageCo2EmissionKgPerTrip,
          averageCo2EmissionKgPerDay: averageCo2EmissionKgPerDay,
          extrapolatedTotalCo2EmissionKgPerTrip: extrapolatedTotalCo2EmissionKgPerTrip,
          extrapolatedTotalCo2EmissionKgPerDay: extrapolatedTotalCo2EmissionKgPerDay,

          extrapolatedTotalConcernedIndividualsCount: extrapolatedTotalConcernedIndividualsCount,
        };
      }

      return extrapolatedPerTransportTypeStats;
    },

    _getGlobalNavigationStats(
        extrapolatedPerTransportTypeStats
    ) {
      let self = this;

      let globalDistanceKmPerTrip = 0;
      let globalDistanceKmPerDay = 0;
      let globalDurationMnPerTrip = 0;
      let globalDurationMnPerDay = 0;
      let globalCo2EmissionKgPerTrip = 0;
      let globalCo2EmissionKgPerDay = 0;
      let globalConcernedIndividualCount = 0;

      for (let [, extrapolatedTransportTypeStats] of Object.entries(
          extrapolatedPerTransportTypeStats
      )) {
        globalDistanceKmPerTrip += extrapolatedTransportTypeStats.extrapolatedTotalDistanceKmPerTrip;
        globalDistanceKmPerDay += extrapolatedTransportTypeStats.extrapolatedTotalDistanceKmPerDay;

        globalDurationMnPerTrip += extrapolatedTransportTypeStats.extrapolatedTotalDurationMnPerTrip;
        globalDurationMnPerDay += extrapolatedTransportTypeStats.extrapolatedTotalDurationMnPerDay;

        globalCo2EmissionKgPerTrip = extrapolatedTransportTypeStats.extrapolatedTotalCo2EmissionKgPerTrip;
        globalCo2EmissionKgPerDay += extrapolatedTransportTypeStats.extrapolatedTotalCo2EmissionKgPerDay;

        globalConcernedIndividualCount += extrapolatedTransportTypeStats.extrapolatedTotalConcernedIndividualsCount;
      }

      let averageDistanceKmPerTrip = this.safeDivision(globalDistanceKmPerTrip, globalConcernedIndividualCount);
      let averageDurationMnPerTrip = this.safeDivision(globalDurationMnPerTrip, globalConcernedIndividualCount);

      let globalDistanceKmPerYear = this.fromDayToYear(globalDistanceKmPerDay);

      let globalWorldToursPerYear = globalDistanceKmPerYear / this.calculusConstants.world_circumference_km;

      let globalCarParkingSpaceImpactM2 = this.calculusConstants.car_parking_area_m2 * this.calculusConstants.car_parking_space_count;

      let globalConcernedIndividualCountForCo2Emission;
      if (!this.useKnownTransportTypesForCalculations) {
        // BEWARE HERE, RESET => we do NOT consider non-CAR values unless these are real-world values!
        globalCo2EmissionKgPerTrip = extrapolatedPerTransportTypeStats.CAR.extrapolatedTotalCo2EmissionKgPerTrip;
        globalCo2EmissionKgPerDay = extrapolatedPerTransportTypeStats.CAR.extrapolatedTotalCo2EmissionKgPerDay;
        globalConcernedIndividualCountForCo2Emission = extrapolatedPerTransportTypeStats.CAR.extrapolatedTotalConcernedIndividualsCount;
      } else {
        globalConcernedIndividualCountForCo2Emission = globalConcernedIndividualCount;
      }
      let globalCo2EmissionKgPerYear = this.fromDayToYear(globalCo2EmissionKgPerDay);
      let averageCo2EmissionKgPerYear = this.safeDivision(globalCo2EmissionKgPerYear, globalConcernedIndividualCountForCo2Emission);

      let _globalCarDistanceKmPerYear = this.fromDayToYear(extrapolatedPerTransportTypeStats.CAR.extrapolatedTotalDistanceKmPerDay);
      let globalCarEnergyConsumptionKwhPerYear = _globalCarDistanceKmPerYear * this.calculusConstants.car_energy_kwh_per_km;
      let globalCarEnergyConsumptionDesktopM2Equivalent = globalCarEnergyConsumptionKwhPerYear / this.calculusConstants.desktop_energy_consumption_yearly_kwh_per_m2;

      return {
        globalConcernedIndividualCount: globalConcernedIndividualCount,

        averageDistanceKmPerTrip: averageDistanceKmPerTrip,
        globalDistanceKmPerTrip: globalDistanceKmPerTrip,
        globalDistanceKmPerDay: globalDistanceKmPerDay,
        globalDistanceKmPerYear: globalDistanceKmPerYear,
        globalWorldToursPerYear: globalWorldToursPerYear,

        averageDurationMnPerTrip: averageDurationMnPerTrip,
        globalDurationMnPerTrip: globalDurationMnPerTrip,  // FIXME are these even useful?
        globalDurationMnPerDay: globalDurationMnPerDay,
        // Need need for yearly or average values for DurationMn

        globalCo2EmissionKgPerTrip: globalCo2EmissionKgPerTrip,
        globalCo2EmissionKgPerDay: globalCo2EmissionKgPerDay,
        globalCo2EmissionKgPerYear: globalCo2EmissionKgPerYear,
        averageCo2EmissionKgPerYear: averageCo2EmissionKgPerYear,

        globalCarEnergyConsumptionKwhPerYear: globalCarEnergyConsumptionKwhPerYear,
        globalCarEnergyConsumptionDesktopM2Equivalent: globalCarEnergyConsumptionDesktopM2Equivalent,
        globalCarParkingSpaceImpactM2: globalCarParkingSpaceImpactM2,
      };
    },

    _getCostStatsForEmployerAndIndividual(
        extrapolatedPerTransportTypeStats,
        globalConcernedIndividualCount,
    ) {

      // YEARLY COSTS PER TRANSPORT TYPE, FOR EMPLOYER //

      let carYearlyCostEuroForEmployer;
      if (this.calculusConstants.car_mileage_allowance_euros_per_km)
        carYearlyCostEuroForEmployer = this.fromDayToYear(
            extrapolatedPerTransportTypeStats.CAR.extrapolatedTotalDistanceKmPerDay *
            this.calculusConstants.car_mileage_allowance_euros_per_km
        );
      else
        carYearlyCostEuroForEmployer =
            extrapolatedPerTransportTypeStats.CAR.extrapolatedTotalConcernedIndividualsCount *
            this.calculusConstants.car_yearly_compensation_package_euros;

      carYearlyCostEuroForEmployer += this.calculusConstants.car_parking_space_count * this.calculusConstants.car_parking_space_price_euros;

      let publicTransportYearlyCostEuroForEmployer =
          (extrapolatedPerTransportTypeStats.PUBLIC_TRANSPORT.extrapolatedTotalConcernedIndividualsCount *
              this.calculusConstants.public_transport_yearly_subscription_price_euros *
              this.calculusConstants.public_transport_subscription_coverage_rate_percent) /
          100;

      let carSharingYearlyCostEuroForEmployer =
          extrapolatedPerTransportTypeStats.CAR_SHARING.extrapolatedTotalConcernedIndividualsCount *
          this.calculusConstants.car_sharing_yearly_fmd_limit_euros;

      let bikeYearlyCostEuroForEmployer =
          extrapolatedPerTransportTypeStats.BIKE.extrapolatedTotalConcernedIndividualsCount *
          this.calculusConstants.bike_and_electric_bike_yearly_fmd_limit_euros;

      let electricBikeYearlyCostEuroForEmployer =
          extrapolatedPerTransportTypeStats.ELECTRIC_BIKE.extrapolatedTotalConcernedIndividualsCount *
          this.calculusConstants.bike_and_electric_bike_yearly_fmd_limit_euros;

      let plevYearlyCostEuroForEmployer =
          extrapolatedPerTransportTypeStats.PLEV.extrapolatedTotalConcernedIndividualsCount *
          this.calculusConstants.plev_yearly_fmd_limit_euros;

      let walkYearlyCostEuroForEmployer =
          extrapolatedPerTransportTypeStats.WALK.extrapolatedTotalConcernedIndividualsCount *
          this.calculusConstants.walk_yearly_allowance_euros;

      let perTransportTypeYearlyCostsEuroForEmployer = {
        CAR: carYearlyCostEuroForEmployer,
        PUBLIC_TRANSPORT: publicTransportYearlyCostEuroForEmployer,
        CAR_SHARING: carSharingYearlyCostEuroForEmployer,
        BIKE: bikeYearlyCostEuroForEmployer,
        ELECTRIC_BIKE: electricBikeYearlyCostEuroForEmployer,
        PLEV: plevYearlyCostEuroForEmployer,
        WALK: walkYearlyCostEuroForEmployer,
      };

      let totalYearlyCostEuroForEmployerForAlternativeTransportTypes = // ALL except the CAR!
          publicTransportYearlyCostEuroForEmployer +
          carSharingYearlyCostEuroForEmployer +
          bikeYearlyCostEuroForEmployer +
          electricBikeYearlyCostEuroForEmployer +
          plevYearlyCostEuroForEmployer +
          walkYearlyCostEuroForEmployer;

      let totalYearlyCostEuroForEmployerForAllTransportTypes = totalYearlyCostEuroForEmployerForAlternativeTransportTypes +
          carYearlyCostEuroForEmployer;

      // YEARLY COSTS PER TRANSPORT TYPE, FOR INDIVIDUAL //

      let carYearlyCostEuroForIndividual = this.fromDayToYear(
          extrapolatedPerTransportTypeStats.CAR.averageDistanceKmPerDay *
          this.calculusConstants.car_cost_euros_per_km
      );
      if (this.calculusConstants.car_mileage_allowance_euros_per_km)
        carYearlyCostEuroForIndividual -= this.fromDayToYear(
            extrapolatedPerTransportTypeStats.CAR.averageDistanceKmPerDay *
            this.calculusConstants.car_mileage_allowance_euros_per_km
        );
      else
        carYearlyCostEuroForIndividual -= this.calculusConstants.car_yearly_compensation_package_euros;

      let publicTransportYearlyCostEuroForIndividual =
          (this.calculusConstants.public_transport_yearly_subscription_price_euros *
              (100 - this.calculusConstants.public_transport_subscription_coverage_rate_percent))
          / 100;

      let carSharingYearlyCostEuroForIndividual =
          this.fromDayToYear(
              (extrapolatedPerTransportTypeStats.CAR_SHARING.averageDistanceKmPerDay *
                  this.calculusConstants.car_cost_euros_per_km) / this.calculusConstants.average_individuals_by_car_sharing
          ) - this.calculusConstants.car_sharing_yearly_fmd_limit_euros;

      let bikeYearlyCostEuroForIndividual =
          150 - this.calculusConstants.bike_and_electric_bike_yearly_fmd_limit_euros; // ARBITRARY YEARLY COST OF A BIKE  FIXME put to data

      let electricBikeYearlyCostEuroForIndividual =
          this.fromDayToYear(0.8 * this.calculusConstants.trips_per_day) - // Hardcoded per-trip cost of E-bike  FIXME move to data section and add Telework
          this.calculusConstants.bike_and_electric_bike_yearly_fmd_limit_euros;

      let walkYearlyCostEuroForIndividual = 0; // Cost of shoes maybe ? LOL

      let perTransportTypeYearlyCostsEuroForIndividual = {
        CAR: carYearlyCostEuroForIndividual,
        PUBLIC_TRANSPORT: publicTransportYearlyCostEuroForIndividual,
        CAR_SHARING: carSharingYearlyCostEuroForIndividual,
        BIKE: bikeYearlyCostEuroForIndividual,
        ELECTRIC_BIKE: electricBikeYearlyCostEuroForIndividual,
        PLEV: null, // ON PURPOSE
        WALK: walkYearlyCostEuroForIndividual,
      };

      let totalWeightedYearlyCost = Object.entries(
          perTransportTypeYearlyCostsEuroForIndividual
      ).reduce(
          (val1, val2) =>
              val1 + val2[1] * extrapolatedPerTransportTypeStats[val2[0]].extrapolatedTotalConcernedIndividualsCount,
          0
      );
      let averageYearlyCostForIndividual = this.safeDivision(totalWeightedYearlyCost, globalConcernedIndividualCount)

      // MANQUE Budget annuel pour les mobilités alternatives

      return {
        perTransportTypeYearlyCostsEuroForEmployer: perTransportTypeYearlyCostsEuroForEmployer,
        totalYearlyCostEuroForEmployerForAlternativeTransportTypes: totalYearlyCostEuroForEmployerForAlternativeTransportTypes,
        totalYearlyCostEuroForEmployerForAllTransportTypes: totalYearlyCostEuroForEmployerForAllTransportTypes,
        perTransportTypeYearlyCostsEuroForIndividual: perTransportTypeYearlyCostsEuroForIndividual,
        averageYearlyCostForIndividual: averageYearlyCostForIndividual,
      };
    },

    onUseCustomModalShareValuesChange() {
      this.useCustomModalShareValuesBackup = this.useCustomModalShareValues;
    },

    resetUserSubmittedCalculusConstants() {
      this.userSubmittedCalculusConstants = Object.assign(
          {},
          this.initialCalculusConstants
      );
    },

    resetUserSubmittedModalSharePercentages() {
      this.userSubmittedModalSharePercentages = Object.assign(
          {},
          this.defaultUserSubmittedModalSharePercentages
      );
    },

    exportToReportingSection() {
      // UNNEEDED, its' quick! openToast("Opération commencée", "Export des statistiques actuelles");
      this.$emit("export-reporting-section", this.getExtractOfStatisticsAndGraphics());
    },

    getExtractOfStatisticsAndGraphics() {

      let theoretical_individual_workforce_for_selected_organizations = this.organizations.reduce((obj1, obj2) => {
        return obj1 + obj2.theoretical_workforce;
      }, 0);

      return {

        statistics_parameters: {
          theoretical_individual_workforce: this.theoreticalIndividualWorkforce,
          theoretical_individual_workforce_for_selected_organizations: theoretical_individual_workforce_for_selected_organizations,
          imported_individual_workforce: this.importedIndividualWorkforce,

          count_of_selected_individuals: this.individuals.length,
          count_of_individuals_having_transport_type: this.countOfIndividualsHavingTransportType,

          theoretical_workforce_extrapolation_factor: this.theoreticalWorkforceExtrapolationFactor,
          use_known_transport_types_for_calculations: this.useKnownTransportTypesForCalculations,
          working_days_per_year: this.workingDaysPerYear,
          use_known_full_telework_days_for_calculations: this.useKnownTeleworkHalfDaysForCalculations,
          full_telework_days_per_week: this.teleworkFullDaysPerWeek,

          selected_organizations: this.organizations,  // Beware, also in reporting_context_summary as "source_organizations"

          used_modal_shares: this.currentlyActiveModalShareStats,
        },
        statistics_numbers: {
          globalDistanceKmPerDay: this.globalNavigationStats.globalDistanceKmPerDay,
          globalDistanceKmPerYear: this.globalNavigationStats.globalDistanceKmPerYear,
          globalWorldToursPerYear: this.globalNavigationStats.globalWorldToursPerYear,

          averageDistanceKmPerTrip: this.globalNavigationStats.averageDistanceKmPerTrip,
          averageDurationMnPerTrip: this.globalNavigationStats.averageDurationMnPerTrip,
          allTripDistancesMedian: this.allTripDistancesMedian,

          // Here we have "Distance trajets domicile-travail / Temps trajets domicile-travail" per transport-type:
          extrapolatedPerTransportTypeStats: this.extrapolatedPerTransportTypeStats,

          globalCo2EmissionKgPerYear: this.globalNavigationStats.globalCo2EmissionKgPerYear,
          globalCarEnergyConsumptionKwhPerYear: this.globalNavigationStats.globalCarEnergyConsumptionKwhPerYear,
          globalCarEnergyConsumptionDesktopM2Equivalent: this.globalNavigationStats.globalCarEnergyConsumptionDesktopM2Equivalent,
          averageCo2EmissionKgPerYear: this.globalNavigationStats.averageCo2EmissionKgPerYear,

          // FIXME here add "Emissions moyennes CO2" per transport-type table?

          // FIXME here add "expert mode fields"?

          totalYearlyCostEuroForEmployerForAllTransportTypes: this.costStatsForEmployerAndIndividual.totalYearlyCostEuroForEmployerForAllTransportTypes,
          totalYearlyCostEuroForEmployerForAlternativeTransportTypes: this.costStatsForEmployerAndIndividual.totalYearlyCostEuroForEmployerForAlternativeTransportTypes,
          /////perTransportTypeYearlyCostsEuroForEmployerForCar: this.costStatsForEmployerAndIndividual.perTransportTypeYearlyCostsEuroForEmployer.CAR,  // FIXME RENAME THIS
          globalCarParkingSpaceImpactM2: this.globalNavigationStats.globalCarParkingSpaceImpactM2,
          averageYearlyCostForIndividual: this.costStatsForEmployerAndIndividual.averageYearlyCostForIndividual,

          perTransportTypeYearlyCostsEuroForEmployer: this.costStatsForEmployerAndIndividual.perTransportTypeYearlyCostsEuroForEmployer,
          perTransportTypeYearlyCostsEuroForIndividual: this.costStatsForEmployerAndIndividual.perTransportTypeYearlyCostsEuroForIndividual,

          calculusConstants: this.calculusConstants,  // TODO move that to statistics_parameters later?

          carDistanceStatsOrNull: this.carDistanceCategoryOrNullForDataExport,
          allDurationStats: this.carDurationCategoriesForDataExport,
        }
      }
    },

    // BIG AND SMALL UTILITIES HERE BELOW //

    _buildStackedChartDataFromMapColorizers(
        mapColorizerCategories,
        totalIndividualCount
    ) {
      if (!mapColorizerCategories.length) return null;

      let colorMapper = mapColorizerCategories[0].colorMapper; // We assume it's the SAME FOR ALL!

      let transportTypeLabels = mapColorizerCategories.map((obj) => obj.label);

      let categories = Object.keys(mapColorizerCategories[0].colorMapper); // ORDER of keys should be preserved

      let datasets = [];

      for (let category of categories) {
        let dataAbsolute = [];
        let dataPercentage = [];
        let individualCount = 0;
        let datasetLabel = "";

        for (let mapColorizer of mapColorizerCategories) {
          let concernedOption = mapColorizer.criteria.filter(
              (criterion) => criterion.slug == category
          );

          if (concernedOption.length) {
            datasetLabel = concernedOption[0].label;
            individualCount = concernedOption[0].individualCount;
          }

          dataAbsolute.push(individualCount);
          dataPercentage.push(
              this.round((individualCount / totalIndividualCount) * 100)
          );
        }

        datasets.push({
          label: datasetLabel,
          backgroundColor: colorMapper[category],
          data: dataPercentage,
        });
      }

      let res = {
        labels: transportTypeLabels,
        datalabels: null,
        datasets: datasets,
      };

      return res;
    },

    round(value) {
      return Math.round(value); // returns a STRING
    },

    roundForDisplay(value) {
      if (value == null) return "ND";
      return this.formatThousandsSeparator(this.round(value));
    },

    toFixed(value) {
      if (value == null) return "ND";
      return this.formatThousandsSeparator(value.toFixed(2).replace(".", ",")); // returns a STRING
    },

    formatThousandsSeparator(value) {
      value = value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
      return this.makeSpacesUnbreakable(value);
    },

    makeSpacesUnbreakable(value) {
      return value.replace(" ", "\xa0");
    },


    isValidPositiveNumber(value) {
      return typeof value == "number" && value >= 0;
    },

    getMedian(arr) {
      if (!arr.length) return 0;
      let middle = Math.floor(arr.length / 2);
      arr = [...arr].sort((a, b) => a - b);
      return arr.length % 2 !== 0
          ? arr[middle]
          : (arr[middle - 1] + arr[middle]) / 2;
    },

    preventNegativeValue(value) {  // FIXME only works for INTEGER variables, no coercion occurs
      if (typeof value == "string" || value instanceof String)
          // Madness - https://stackoverflow.com/questions/203739/why-does-instanceof-return-false-for-some-literals
        return 0;

      if (value < 0) return 0;

      return value;
    },

    fromDayToYear(value) {
      return value * this.workingDaysPerYear;
    },

    safeDivision(a, b, fallback) {
      if (!b) return fallback || 0;
      return a / b;
    },

    getPercentageArray(arrayOfValues) {
      let totalSum = arrayOfValues.reduce((val1, val2) => val1 + val2, 0);
      return arrayOfValues.map((val) =>
          this.round(totalSum ? (val / totalSum) * 100 : 0)
      );
    },
  },
};
</script>

<style lang="scss"></style>
